/**
 * On-the-wire OneSignal push payload contract.
 *
 * Extension rules — read before adding fields:
 *   1. To add a new capability tier (priority level, live-activity update,
 *      etc.), add a TOP-LEVEL optional field on PushPayloadSchema. Do not
 *      nest new capabilities inside existing fields.
 *   2. Producers must feature-detect before populating new fields.
 *   3. Mobile-side handlers must tolerate unknown values on the reserved
 *      enum-shaped fields (e.g., a future `priority: 'live'` value should
 *      fall back to the default-priority handler, not throw).
 *   4. The schema is `.strict()` at the top level — fields not listed here
 *      are rejected at parse time. Add them here BEFORE producers ship them.
 *   5. Legacy fields carry `@deprecated` and remain valid for back-compat
 *      with payloads already on locked devices / in OneSignal queues. Do
 *      not remove until a full mobile release cycle has passed AND a
 *      migration audit confirms zero traffic on the legacy shape.
 */

import { z } from 'zod'

/**
 * Tenant-isolation invariant only. `.passthrough()` is intentional — this
 * schema is used as a fast-fail context check on the FULL payload object;
 * we want to validate context fields without rejecting other fields at
 * this layer. Strict full-payload validation runs separately against
 * PushPayloadSchema.
 */
const RequiredContextShape = {
  schoolId: z.string().min(1),
  schoolPublicId: z.string().min(1)
} as const

export const RequiredContextSchema = z.object(RequiredContextShape).passthrough()

export type RequiredContext = z.infer<typeof RequiredContextSchema>

/**
 * Identifier shape allowed in push-payload entity fields. Conservative
 * alphanumeric + `_` / `-` (CUID, CUID2, UUID, ULID, KSUID, slug all match).
 * 128 chars covers every id format we currently mint with margin.
 *
 * This is a TRUST-BOUNDARY regex — the entity id flows into route paths
 * with template interpolation in `apps/mobile/src/services/notifications/
 * routing/entity-resolver.ts`. Anything that lets `/`, `..`, `?`, `&`,
 * `#`, or whitespace through reopens the path-injection finding.
 */
const EntityIdRegex = /^[A-Za-z0-9_-]{1,128}$/

/**
 * Stream chat channel types accepted from push payload. Today the backend
 * only ever emits `'messaging'` (see `apps/backend/src/services/stream-
 * chat/group-channel-manager.ts`) — the rest of the Stream defaults are
 * listed so a future channel-type rollout doesn't immediately break
 * push-driven chat deep links on already-installed devices. New values
 * must be added here BEFORE producers ship them.
 */
const ChatChannelTypeSchema = z.enum(['messaging', 'team', 'livestream', 'commerce', 'gaming'])

export const EntitySchema = z
  .object({
    type: z.enum(['group', 'person', 'tab', 'activity', 'assignment', 'chat']),
    id: z.string().regex(EntityIdRegex),
    groupId: z.string().regex(EntityIdRegex).optional(),
    contentId: z.string().regex(EntityIdRegex).optional(),
    channelType: ChatChannelTypeSchema.optional(),
    messageId: z.string().regex(EntityIdRegex).optional()
  })
  .strict()

export type Entity = z.infer<typeof EntitySchema>

export const IntentSchema = z.enum(['view', 'view_members', 'profile', 'contact'])
export type Intent = z.infer<typeof IntentSchema>

const TabRegex = /^[a-z0-9-]+$/

/**
 * Allow-list of legacy `route` field prefixes. The legacy field is
 * deprecated — it predates the entity+intent pattern — but in-flight
 * payloads on locked devices and OneSignal queues still carry it.
 *
 * IMPORTANT: This regex is the TRUST BOUNDARY for "push payload can land
 * a user anywhere in the app." Anything that matches lands as a literal
 * `router.push(route)`. The original regex (`^/(?!.*\.\.)[^\s]+$`) only
 * blocked parent-dir traversal and let `/(secure)/(modals)/admin/*` and
 * any other internal screen through.
 *
 * Add prefixes here only with an explicit reason. `(secure)/(modals)/
 * admin/*`, `(secure)/(modals)/debug/*`, and ANY route group containing
 * "admin" or "debug" MUST NOT be reachable from a push payload.
 */
const RouteRegex =
  /^(?!.*\.\.)\/(?:notifications(?:$|\/)|profile(?:$|\/)|directory(?:$|\/)|calendar(?:$|\/)|news(?:$|\/)|announcement(?:$|\/)|event(?:$|\/)|page(?:$|\/)|chat(?:$|\/)|messages(?:$|\/)|\(tabs\)\/[a-z0-9-]+(?:$|\/)|groups(?:$|\/))[A-Za-z0-9_\-\/?=&%.]*$/

export const PushPayloadSchema = z
  .object({
    ...RequiredContextShape,
    // ─── routing (current) ───
    entity: EntitySchema.optional(),
    intent: IntentSchema.optional(),
    webUrl: z.string().url().optional(),
    webTitle: z.string().optional(),
    externalUrl: z.string().url().optional(),
    tab: z.string().regex(TabRegex).optional(),
    route: z.string().regex(RouteRegex).optional(),

    // ─── reserved (extensibility) ───
    /** Reserved for future priority tiers (e.g., 'time-sensitive', 'live'). */
    priority: z.string().optional(),
    /** Reserved for future Live Activity updates. */
    liveActivity: z.record(z.string(), z.unknown()).optional(),
    /** Reserved for Track G correlation IDs. */
    correlationId: z.string().optional(),

    // ─── legacy (back-compat with in-flight payloads) ───
    /** @deprecated legacy routing — prefer entity+intent */
    groupId: z.string().optional(),
    /** @deprecated legacy routing — prefer entity+intent */
    personId: z.string().optional(),
    /** @deprecated legacy routing — prefer entity+intent */
    activityId: z.string().optional(),
    /** @deprecated legacy routing — prefer entity+intent */
    activityIds: z.array(z.string()).optional(),
    /** @deprecated legacy routing — prefer entity+intent */
    action: z.string().optional(),
    /** @deprecated legacy discriminator — prefer entity.type */
    type: z.string().optional(),
    /** @deprecated legacy routing — prefer entity+intent */
    announcementId: z.string().optional(),
    /** @deprecated legacy routing — prefer entity+intent */
    eventId: z.string().optional(),
    /** @deprecated legacy routing — prefer entity+intent */
    eventDate: z.string().optional(),
    /** @deprecated legacy routing — prefer entity+intent */
    assignmentId: z.string().optional(),
    /** @deprecated legacy routing — prefer entity+intent */
    dueDate: z.string().optional(),
    /** @deprecated legacy routing — prefer entity+intent */
    grade: z.string().optional(),
    /** @deprecated legacy discriminator — prefer entity.type */
    notificationType: z.string().optional(),

    // ─── content-notification fields (back-compat with current production traffic) ───
    /** @deprecated content notification — prefer entity+intent */
    contentId: z.string().optional(),
    /** @deprecated content notification — prefer entity+intent */
    contentType: z.string().optional(),
    /** @deprecated content notification — prefer entity+intent (deep link URL the mobile router resolved off of) */
    deepLink: z.string().optional(),
    /** @deprecated content notification action buttons */
    actionButtons: z
      .array(
        z
          .object({
            id: z.string(),
            text: z.string()
          })
          .strict()
      )
      .optional(),

    // ─── broadcast / diagnostic / social fields (legacy back-compat) ───
    /** @deprecated broadcast id — prefer entity+intent */
    broadcastId: z.string().optional(),
    /** @deprecated diagnostic / test push timestamp */
    timestamp: z.string().optional(),
    /** @deprecated section id from assignment notifications */
    sectionId: z.string().optional(),
    /** @deprecated event-source tag from assignment notifications */
    eventSource: z.string().optional(),
    /** @deprecated comment id (social/content/feed comment notifications) */
    commentId: z.string().optional(),
    /** @deprecated reaction type (chat/content/comment/feed reactions) */
    reactionType: z.string().optional(),
    /** @deprecated stream chat channel id (chat reaction notifications) */
    channelId: z.string().optional(),
    /** @deprecated stream chat message id (chat reaction notifications) */
    messageId: z.string().optional(),

    // ─── event-handler legacy fields (assignment-due-soon, user-joined-school) ───
    /** @deprecated event-handler legacy field */
    studentId: z.string().optional(),
    /** @deprecated event-handler legacy field */
    userId: z.string().optional(),

    // ─── test-notification legacy fields (send-test-notification) ───
    /** @deprecated test notification metadata */
    testType: z.string().optional(),
    /** @deprecated test notification metadata */
    userPublicId: z.string().optional(),
    /** @deprecated test notification metadata */
    appBundleId: z.string().nullable().optional(),
    /** @deprecated iOS NSE flag for time-sensitive test pushes */
    TIME_SENSITIVE: z.string().optional(),
    /** @deprecated iOS NSE flag for critical test pushes */
    CRITICAL: z.string().optional()
  })
  .strict()

export type PushPayload = z.infer<typeof PushPayloadSchema>
